import { Injectable } from "@angular/core";
import { Router, Routes } from "@angular/router";
import { RolePermissionsService } from "../role-permissions/role-permissions.service";
import { Observable, combineLatest, of } from "rxjs";
import { MenuDTO } from "src/app/models/Menu/MenuDTO";
import { map } from "rxjs/operators";

@Injectable({ providedIn: "root" })
export class NavigationService {
  private routeMapByPath = new Map<string, any>();
  private routeMapByCode = new Map<string, any>();

  constructor(
    private router: Router,
    private rolePermissionService: RolePermissionsService,
  ) {
    this.buildRouteMap(this.router.config);
  }

  private buildRouteMap(routes: Routes, parentPath = "") {
    for (const route of routes) {
      const fullPath = route.path
        ? `${parentPath}/${route.path}`.replace("//", "/")
        : parentPath;

      if (route.data) {
        this.routeMapByPath.set(route.path, route.data);
      }

      if (route.data?.code) {
        this.routeMapByCode.set(route.data.code, route);
      }

      if (route.children) {
        this.buildRouteMap(route.children, fullPath);
      }
    }
  }

  isPathAllowed(path: string): boolean {
    const data = this.routeMapByPath.get(path);

    if (!data?.roleCode || !data?.permissionCode) return true;

    return this.rolePermissionService.hasPermissionSync(
      data.roleCode,
      data.permissionCode,
    );
  }

  private isMenuAllowed(menu: MenuDTO): boolean {
    const route = this.router.config.find((r) => r.data?.code === menu.code);

    if (!route?.data) return false;

    return this.rolePermissionService.hasPermissionSync(
      route.data.roleCode,
      route.data.permissionCode,
    );
  }

  flattenMenus(menus: any[]): any[] {
    if (!menus?.length) return [];
    const result: any[] = [];

    for (const item of menus) {
      if (item?.code === "folder" && item?.subMenus) {
        result.push(...this.flattenMenus(item.subMenus));
      } else {
        result.push(item);
      }
    }

    return result;
  }

  getFirstAllowedRoute(menus: any[]): string | null {
    const flatMenus = this.flattenMenus(menus);
    const first = flatMenus.find((menu) => this.isMenuAllowed(menu));

    return first ? this.routeMapByCode.get(first.code).path : null;
  }

  resolveInitialRoute(basePath: string, menus: any[]): string {
    if (basePath && this.isPathAllowed(basePath)) {
      return basePath;
    }

    const first = this.getFirstAllowedRoute(menus);

    return first || "/welcome";
  }

  resolveInitialRoute$(
    basePath: string,
    menus$: Observable<MenuDTO[]>,
  ): Observable<string> {
    if (localStorage.getItem("isAuthenticated") !== "true") {
      return of("/login");
    }

    return combineLatest([menus$, this.rolePermissionService["roles$"]]).pipe(
      map(([menus]) => this.resolveInitialRoute(basePath, menus ?? [])),
    );
  }
}
